/**
 * Per-user UI preferences hook.
 *
 * Backed by ``GET /api/v1/me/preferences`` and a debounced
 * ``PUT /api/v1/me/preferences`` so UI state - currently the agent
 * inbox filter selections - survives logout, browser change, and
 * machine change.
 *
 * Why a debounced PUT: agents drag sliders / type into search boxes
 * which can fire dozens of state changes per second. We coalesce
 * those into one network round-trip per quiet 500ms window.
 *
 * The hook ALSO mirrors the blob into localStorage under
 * ``buyist-support:user-preferences``. That cache lets the first
 * paint of a page show the user's most-recent saved filters without
 * waiting for ``/me/preferences`` to round-trip - especially useful
 * for the agent inbox which would otherwise flash the default
 * "Open only" view for ~100ms on every navigation.
 *
 * The server is the source of truth. The localStorage cache is only
 * consulted before the server response lands; once the query
 * resolves, server values win on conflict.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import api from "@/lib/api";

export const USER_PREFERENCES_QUERY_KEY = ["me", "preferences"] as const;
const LOCAL_CACHE_KEY = "buyist-support:user-preferences";
const PUT_DEBOUNCE_MS = 500;

/** Free-form per-user UI state. Keys are dot-namespaced by surface. */
export type UserPreferences = Record<string, unknown>;

/** Best-effort read of the localStorage cache. Returns ``{}`` on any
 * parse failure - corruption should not break first paint. */
function readLocalCache(): UserPreferences {
  try {
    const raw = localStorage.getItem(LOCAL_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as UserPreferences)
      : {};
  } catch {
    return {};
  }
}

function writeLocalCache(prefs: UserPreferences): void {
  try {
    localStorage.setItem(LOCAL_CACHE_KEY, JSON.stringify(prefs));
  } catch {
    /* quota / private-mode browsers - the server still has the truth */
  }
}

/**
 * Read the current user's preferences. Returns the localStorage
 * warm-start blob immediately (``placeholderData``) so the consumer
 * never sees ``undefined`` on first render. Once the server
 * response lands, the query updates and the localStorage cache is
 * refreshed.
 */
export function useUserPreferences(): UseQueryResult<UserPreferences> {
  const query = useQuery<UserPreferences>({
    queryKey: USER_PREFERENCES_QUERY_KEY,
    queryFn: async () => {
      const response = await api.get<UserPreferences>("/me/preferences");
      return response.data ?? {};
    },
    placeholderData: readLocalCache,
    // Stale-while-revalidate. UI prefs are read often; the bandwidth is
    // a single tiny JSON object per refetch.
    staleTime: 5 * 60_000,
  });

  // Mirror successful server reads into the localStorage cache so the
  // next page navigation paints with the freshest known value.
  useEffect(() => {
    if (query.isSuccess && query.data) {
      writeLocalCache(query.data);
    }
  }, [query.isSuccess, query.data]);

  return query;
}

/**
 * Debounced setter for the preferences blob. Updates the React Query
 * cache + the localStorage cache immediately so subsequent reads see
 * the new value, and schedules a single PUT after ``PUT_DEBOUNCE_MS``
 * of quiet (resets on every call).
 *
 * Returns a mutation result so callers can surface pending / error
 * states if they care; most callers fire-and-forget.
 */
export function useSaveUserPreferences(): UseMutationResult<
  UserPreferences,
  Error,
  UserPreferences
> & { saveDebounced: (next: UserPreferences) => void } {
  const queryClient = useQueryClient();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<UserPreferences | null>(null);

  const mutation = useMutation({
    mutationFn: async (prefs: UserPreferences) => {
      const response = await api.put<UserPreferences>(
        "/me/preferences",
        prefs,
      );
      return response.data;
    },
    onSuccess: (saved) => {
      queryClient.setQueryData(USER_PREFERENCES_QUERY_KEY, saved);
      writeLocalCache(saved);
    },
  });

  const saveDebounced = (next: UserPreferences) => {
    // Optimistic: update cache + localStorage immediately so the
    // app reads the new value even before the network roundtrip.
    queryClient.setQueryData(USER_PREFERENCES_QUERY_KEY, next);
    writeLocalCache(next);
    pendingRef.current = next;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const toSend = pendingRef.current;
      pendingRef.current = null;
      timerRef.current = null;
      if (toSend) mutation.mutate(toSend);
    }, PUT_DEBOUNCE_MS);
  };

  // Flush any pending write on unmount so closing the tab during the
  // debounce window does not silently lose the agent's last change.
  // We send the pending value synchronously via ``mutate`` so the
  // request fires before React tears down.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      const toSend = pendingRef.current;
      pendingRef.current = null;
      if (toSend) mutation.mutate(toSend);
    };
    // The mutation reference is stable across renders (TanStack
    // useMutation), so re-running this effect on every render would
    // just install identical cleanup. We deliberately want the cleanup
    // to fire only at unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return Object.assign(mutation, { saveDebounced });
}

// ---------------------------------------------------------------------
// Slot helpers (typed accessors per UI surface)
// ---------------------------------------------------------------------

/** Read a single dot-namespaced slot from a preferences blob, with a
 * type cast escape hatch. Returns ``undefined`` if the key is missing
 * or the value is not an object - callers should fall back to their
 * default shape. */
export function getPreferenceSlot<T>(
  prefs: UserPreferences | undefined,
  key: string,
): T | undefined {
  if (!prefs) return undefined;
  const v = prefs[key];
  if (v === undefined || v === null) return undefined;
  return v as T;
}

/** Return a new preferences blob with one slot replaced. Use this in
 * place of an in-place mutation so React's setState change-detection
 * still sees the update. */
export function setPreferenceSlot<T>(
  prefs: UserPreferences | undefined,
  key: string,
  value: T,
): UserPreferences {
  return { ...(prefs ?? {}), [key]: value as unknown };
}
